import type { ComponentType } from "react";

/**
 * Table de routes unique, consommée par trois endroits :
 *  - AppShell (client) : chaque entrée devient un React.lazy
 *  - entry-server      : chaque entrée est résolue en amont pour le prerender
 *  - main.tsx          : précharge la route courante avant d'hydrater
 *
 * Toute route ajoutée ici doit l'être aussi dans src/seo/routes.ts (métadonnées
 * + émission du HTML prérendu) et dans public/sitemap.xml.
 */
export type RouteDef = {
  path: string;
  load: () => Promise<{ default: ComponentType }>;
};

export const ROUTES: RouteDef[] = [
  { path: "/", load: () => import("./pages/HomePage") },
  { path: "/gradient-descent", load: () => import("./pages/GradientDescentPage") },
  { path: "/compiler", load: () => import("./pages/CompilerPage") },
  { path: "/lakehouse", load: () => import("./pages/LakehousePage") },
  { path: "/mcp", load: () => import("./pages/MCPPage") },
  { path: "/llm-vram-calculator", load: () => import("./pages/LLMVramCalculatorPage") },
  // Une page « VRAM requirements » par modèle du calculateur (liste : src/seo/vramModels.ts).
  // Pas sous /llm-vram-calculator/ : nginx (try_files $uri $uri/ …) servirait alors
  // le dossier au lieu de llm-vram-calculator.html, soit un 403 sur la page principale.
  { path: "/vram-requirements/:slug", load: () => import("./pages/VramModelPage") },
  // Prix de location : récapitulatif + une page par GPU (src/seo/gpuPrices.ts). Chemins
  // distincts (/gpu-prices vs /gpu-price/…) pour la même raison nginx que ci-dessus.
  { path: "/gpu-prices", load: () => import("./pages/GpuPricesPage") },
  { path: "/gpu-price/:slug", load: () => import("./pages/GpuPricePage") },
  { path: "/ai-ml-architect", load: () => import("./pages/AIMLArchitectPage") },
  // Catch-all : nginx renvoie déjà un vrai 404 pour ces URLs, cette route couvre
  // la navigation côté client.
  { path: "*", load: () => import("./pages/NotFoundPage") },
];

/** Route correspondant à un chemin (exact, ou préfixe d'une route « …/:slug »), catch-all en dernier recours. */
export function matchRoute(pathname: string): RouteDef {
  return (
    ROUTES.find((r) => r.path === pathname) ??
    ROUTES.find((r) => r.path.endsWith("/:slug") && pathname.startsWith(r.path.slice(0, -":slug".length))) ??
    ROUTES.find((r) => r.path === "*")!
  );
}
